import React from 'react';
import { t } from 'i18next';
import moment from 'moment';
import {
  Box,
  Card,
  Stack,
  Button,
  Divider,
  Typography,
  CardHeader,
  CardContent,
  CardActions,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import Iconify from '../../../media/iconify';
import { MilestoneStatusLabel } from '../../../core/milestone-status-label';
import { ChoicesStatusMilestone } from '@/types';
import { getTotal } from '@/utils/billing/billing-milestones-snackbar';
import { RHFNumberText } from '../../../forms/hook-form/rhf-number-field';
import { IMilestoneSelected, MILESTONES_PER_PAGE } from '../types';

interface ISelectedMilestonesPanelProps {
  selectedMilestones: IMilestoneSelected[];
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  onRemoveMilestone: (milestoneId: string) => void;
  onClearSelection: () => void;
}

export function SelectedMilestonesPanel({
  selectedMilestones,
  currentPage,
  setCurrentPage,
  onRemoveMilestone,
  onClearSelection,
}: ISelectedMilestonesPanelProps) {
  const theme = useTheme();

  // Maintain the consistent order based on payment date and ID
  const sortedMilestones = React.useMemo(
    () =>
      [...selectedMilestones].sort((a, b) => {
        const dateA = moment(a.paymentDate);
        const dateB = moment(b.paymentDate);

        if (!dateA.isSame(dateB, 'day')) {
          return dateA.isBefore(dateB) ? -1 : 1;
        }

        return a._id.localeCompare(b._id);
      }),
    [selectedMilestones]
  );

  const totalPages = Math.ceil(sortedMilestones.length / MILESTONES_PER_PAGE);
  const startIndex = (currentPage - 1) * MILESTONES_PER_PAGE;
  const endIndex = startIndex + MILESTONES_PER_PAGE;
  const currentMilestones = sortedMilestones.slice(startIndex, endIndex);

  const milestoneTotal = getTotal(selectedMilestones);

  return (
    <Card>
      <CardHeader title={`${t('Selected Milestones')} (${sortedMilestones.length})`} />

      <CardContent>
        {sortedMilestones.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
            {t('No milestones were selected')}
          </Typography>
        ) : (
          <>
            <Stack spacing={2}>
              {currentMilestones.map((milestone: IMilestoneSelected, index: number) => (
                <Box
                  key={`${milestone._id}-${startIndex + index}`}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
                    boxShadow: theme.customShadows?.z1,
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" noWrap>
                        {`${milestone.orderItem?.product?.name}`}
                      </Typography>
                      <Typography variant="subtitle2" noWrap>
                        {`${milestone.orderItem?.plan?.name}`}
                      </Typography>
                    </Box>
                    <Iconify
                      icon="eva:close-fill"
                      onClick={() => onRemoveMilestone(milestone._id)}
                      sx={{ cursor: 'pointer', fontSize: 18, color: 'text.secondary' }}
                    />
                  </Stack>

                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', mb: 1, display: 'block' }}
                  >
                    {moment(milestone.paymentDate).utc().format('L')}
                  </Typography>

                  <Box sx={{ mb: 1 }}>
                    <MilestoneStatusLabel status={milestone.status as ChoicesStatusMilestone} />
                  </Box>

                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    ${milestone.total?.toLocaleString('en-US')}
                  </Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {milestone.orderItem?.order?.number && (
                      <Typography
                        variant="caption"
                        sx={{ color: theme.palette.primary.main, fontWeight: 500 }}
                      >
                        {t('PO')}–N°{milestone.orderItem.order.number.toString().padStart(3, '0')}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              ))}
            </Stack>

            {sortedMilestones.length > MILESTONES_PER_PAGE && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  mt: 3,
                  gap: 1,
                }}
              >
                <Iconify
                  icon="eva:arrow-left-fill"
                  onClick={() => setCurrentPage((prev: number) => Math.max(1, prev - 1))}
                  sx={{
                    cursor: currentPage > 1 ? 'pointer' : 'not-allowed',
                    fontSize: 16,
                    color: currentPage > 1 ? 'text.primary' : 'text.disabled',
                  }}
                />
                <Typography variant="body2" sx={{ color: 'text.secondary', mx: 1 }}>
                  {currentPage} / {totalPages}
                </Typography>
                <Iconify
                  icon="eva:arrow-right-fill"
                  onClick={() => setCurrentPage((prev: number) => Math.min(totalPages, prev + 1))}
                  sx={{
                    cursor: currentPage < totalPages ? 'pointer' : 'not-allowed',
                    fontSize: 16,
                    color: currentPage < totalPages ? 'text.primary' : 'text.disabled',
                  }}
                />
              </Box>
            )}
          </>
        )}
      </CardContent>

      <CardActions>
        <Stack direction="column" sx={{ width: '100%' }} spacing={1}>
          <Divider sx={{ my: 0 }} />
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{ width: '100%', fontWeight: 500 }}
          >
            <Typography variant="body2" color="text.secondary">
              {t('Subtotal')}
            </Typography>
            <RHFNumberText value={milestoneTotal} decimalScale={2} fixedDecimalScale />
          </Stack>

          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{ width: '100%', fontWeight: 500 }}
          >
            <Typography variant="h6">{t('Total')}</Typography>
            <Typography variant="h6">
              <RHFNumberText value={milestoneTotal} decimalScale={2} fixedDecimalScale />
            </Typography>
          </Stack>

          <Divider sx={{ my: 0 }} />

          <Button variant="text" color="primary" onClick={onClearSelection}>
            <Iconify icon="solar:trash-bin-minimalistic-bold-duotone" /> {t('Clear selection')}
          </Button>
        </Stack>
      </CardActions>
    </Card>
  );
}
